import { useState } from 'react';
import { Button, Card, EmptyState, Field, Status } from '@gatecheckpro/design-system';
import {
  inventoryPoolCodeSchema,
  type OrganizerEvent,
  type SeatingInventory,
} from '@gatecheckpro/contracts';
import { useCommand } from '../../lib/useCommand.ts';
import { FailureNotice } from '../../lib/refusal.tsx';
import { eligibilityFor, INELIGIBILITY_NOTE } from '../../lib/seating/eligibility.ts';

/**
 * The capacity a night's reserved seats are sold from.
 *
 * Set aside per session and per product, because that is what makes "12A is
 * free on Friday" and "12A is free on Saturday" two answerable questions rather
 * than one. The organizer supplies the human parts — a name, a code, how many
 * chairs — and never chooses whether this is reserved-seat inventory: the fact
 * that this command created it is what decides that.
 *
 * Capacity is stated here and checked again at activation against the chairs
 * actually put on sale. The surface does not calculate that comparison; if the
 * two disagree the server says so, with both of its own numbers.
 */
export function SeatingInventoryPanel({
  base,
  event,
  eventSessionId,
  inventory,
  onProvisioned,
}: {
  readonly base: string;
  readonly event: OrganizerEvent;
  readonly eventSessionId: string;
  readonly inventory: readonly SeatingInventory[];
  readonly onProvisioned: () => void;
}) {
  const command = useCommand<SeatingInventory>();
  const [productId, setProductId] = useState('');
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [capacity, setCapacity] = useState('');

  const eligibility = eligibilityFor(event.ticketProducts, eventSessionId);
  const usable = eligibility.filter((entry) => entry.eligible);
  const ineligible = eligibility.filter((entry) => !entry.eligible);
  const codeProblem =
    code === '' || inventoryPoolCodeSchema.safeParse(code).success
      ? undefined
      : 'Capitals, numbers and underscores, starting with a letter.';

  return (
    <Card title="Capacity for this night" eyebrow={`${String(inventory.length)} set aside`}>
      <div className="gc-stack">
        <FailureNotice error={command.error} />

        {inventory.length === 0 ? (
          <EmptyState title="No reserved-seat capacity yet">
            Reserved seats are sold from capacity set aside for this night and this product. Without
            it there is nothing for a chair to draw on, and nothing can go on sale.
          </EmptyState>
        ) : (
          <div className="gc-table-scroll">
            <table className="gc-table">
              <thead>
                <tr>
                  <th scope="col">Code</th>
                  <th scope="col">Name</th>
                  <th scope="col">Sold as</th>
                  <th scope="col">Chairs</th>
                </tr>
              </thead>
              <tbody>
                {inventory.map((row) => (
                  <tr key={row.inventoryAllocationId}>
                    <td className="gc-mono">{row.code}</td>
                    <td>{row.displayName}</td>
                    <td>
                      {event.ticketProducts.find(
                        (product) => product.ticketProductId === row.ticketProductId,
                      )?.currentVersion.displayName ?? row.ticketProductId.slice(0, 8)}
                    </td>
                    <td className="gc-mono">{row.capacity.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/*
          The products that cannot back this night, always — not only when
          nothing can. A shorter dropdown with no explanation is the failure
          this list exists to prevent, and it is *more* confusing when some
          products are offered and others silently are not.
        */}
        {ineligible.length > 0 && (
          <div className="gc-stack gc-stack--tight">
            <p className="gc-muted">
              {usable.length === 0
                ? 'No product on this event can back reserved seating for this night yet. A reserved seat belongs to one night, so it needs a product of its own.'
                : 'Not every product can back reserved seating for this night:'}
            </p>
            <ul
              className="gc-stack gc-stack--tight"
              style={{ paddingInlineStart: '1.1rem' }}
              data-testid="ineligible-products"
            >
              {ineligible.map(({ product, reason }) => (
                <li key={product.ticketProductId} className="gc-muted">
                  <span className="gc-mono">{product.code}</span> — {INELIGIBILITY_NOTE[reason!]}
                </li>
              ))}
            </ul>
          </div>
        )}

        {usable.length > 0 && (
          <form
            className="gc-stack"
            onSubmit={(formEvent) => {
              formEvent.preventDefault();
              void command
                .run(`${base}/event-sessions/${eventSessionId}/seating/inventory`, {
                  method: 'POST',
                  body: {
                    ticketProductId: productId,
                    code,
                    displayName,
                    capacity: Number(capacity),
                  },
                })
                .then((result) => {
                  if (result === null) return;
                  setCode('');
                  setDisplayName('');
                  setCapacity('');
                  onProvisioned();
                });
            }}
          >
            <div className="gc-grid">
              <div className="gc-field">
                <label className="gc-field__label" htmlFor="inventoryProduct">
                  Sold as
                </label>
                <select
                  id="inventoryProduct"
                  className="gc-select"
                  value={productId}
                  onChange={(input) => setProductId(input.target.value)}
                >
                  <option value="">Choose a product</option>
                  {usable.map(({ product }) => (
                    <option key={product.ticketProductId} value={product.ticketProductId}>
                      {product.currentVersion.displayName} ({product.code})
                    </option>
                  ))}
                </select>
              </div>
              <Field
                label="Code"
                placeholder="FRI_RESERVED"
                required
                error={codeProblem}
                value={code}
                onChange={(input) => setCode(input.target.value.toUpperCase())}
              />
              <Field
                label="Name"
                placeholder="Friday reserved"
                required
                value={displayName}
                onChange={(input) => setDisplayName(input.target.value)}
              />
              <Field
                label="Chairs"
                type="number"
                min={1}
                required
                hint="Checked against the chairs you put on sale, when this night goes live."
                value={capacity}
                onChange={(input) => setCapacity(input.target.value)}
              />
            </div>
            <div className="gc-row">
              <Button
                type="submit"
                variant="primary"
                busy={command.busy}
                disabled={
                  productId === '' ||
                  code === '' ||
                  displayName === '' ||
                  capacity === '' ||
                  codeProblem !== undefined
                }
              >
                Set capacity aside
              </Button>
              <Status
                tone="neutral"
                glyph="◇"
                label="Reserved seating"
                title="This command only ever creates reserved-seat capacity. It is not a choice."
              />
            </div>
          </form>
        )}
      </div>
    </Card>
  );
}
